<template>
  <StandardButton
    :id="id"
    v-bind="resolvedButtonProps"
    @click="onButtonClick"
    ><template #default
      ><span>{{ resolvedButtonProps.label }}<span class="mimic-bv-caret" /></span></template
  ></StandardButton>
  <StandardMenu
    ref="menuRef"
    :menu-items="menuItemsWithOptionClass"
    :popup="true"
    :dropdown-align="dropdownAlign"
    :base-z-index="ABOVE_STICKY_HEADER_Z_INDEX"
    @click.stop
  />
</template>

<script lang="ts">
export default {
  name: 'StandardSentenceDropdown',
  compatConfig: { MODE: 3 },
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import type { MenuItem } from 'primevue/menuitem';
import StandardMenu from '@/components/standard-components/StandardMenu.vue';
import { ButtonSize, ButtonVariant, type StandardButtonProps } from '@/types/StandardButtonTypes';
import { ABOVE_STICKY_HEADER_Z_INDEX } from '@/constants/ComponentZIndices';

/** Applied to every menu row so selected-item styles stay scoped to this component. */
const STANDARD_SENTENCE_OPTION_CLASS = 'standard-sentence-option';

const props = withDefaults(
  defineProps<{
    buttonProps: StandardButtonProps;
    menuItems: MenuItem[];
    dropdownAlign?: 'start' | 'end';
    dataTestid?: string;
    id?: string;
  }>(),
  {
    dropdownAlign: 'start',
  },
);

const attrs = useAttrs();
const menuRef = ref<InstanceType<typeof StandardMenu> | null>(null);

const mergeButtonClassProp = (parentClassProp?: StandardButtonProps['classProp']): StandardButtonProps['classProp'] => {
  const classes: string[] = ['font-weight-500', 'border-transparent', 'p-0', 'sentence-dropdown-button'];

  if (typeof attrs.class === 'string' && attrs.class.length > 0) {
    classes.push(attrs.class);
  }

  if (parentClassProp == null) {
    return classes;
  }

  if (typeof parentClassProp === 'string') {
    return [...classes, parentClassProp];
  }

  if (Array.isArray(parentClassProp)) {
    return [...classes, ...parentClassProp];
  }

  return parentClassProp;
};

const resolvedButtonProps = computed((): StandardButtonProps => {
  const { classProp: parentClassProp, ...parentButtonProps } = props.buttonProps;

  return {
    variant: ButtonVariant.TEXT,
    size: ButtonSize.NORMAL,
    dataTestid: props.dataTestid,
    ...parentButtonProps,
    classProp: mergeButtonClassProp(parentClassProp),
  };
});

const menuItemsWithOptionClass = computed((): MenuItem[] =>
  props.menuItems.map((item) => ({
    ...item,
    class: [item.class, STANDARD_SENTENCE_OPTION_CLASS].filter(Boolean),
  })),
);

const onButtonClick = (event: MouseEvent): void => {
  menuRef.value?.toggle(event);
};
</script>

<style lang="scss">
/**
 * StandardMenu has no selected-option styling. MenuItem `class: 'active'` lands on the
 * `<li>`, while the visible color/background is on the nested link. The menu is also
 * teleported, so this rule cannot be scoped.
 */
.p-menu-item.standard-sentence-option.active .p-menu-item-link {
  color: var(--p-highlight-color);
  background-color: var(--p-highlight-background);
  cursor: pointer;
}

/* PrimeVue's .p-menu hardcodes min-width: 12.5rem, beating Bootstrap's .dropdown-menu (injected later). */
.p-menu.dropdown-menu:has(.standard-sentence-option) {
  min-width: 10rem; // Bootstrap's $dropdown-min-width
}

/* Match Bootstrap .dropdown-item padding, not PrimeVue's menu.item.padding token. */
.p-menu-item.standard-sentence-option .p-menu-item-link {
  padding: 0.25rem 1.25rem;
}

/* Hide StandardMenu's empty fa-fw icon spacer; sentence options never have icons. */
.p-menu-item.standard-sentence-option .p-menu-item-link > .fa-fw:empty {
  display: none;
}

/* PrimeVue's .p-menu-item-label hardcodes line-height: 1; Bootstrap body text uses 1.5. */
.p-menu-item.standard-sentence-option .p-menu-item-label {
  line-height: 1.5; // Bootstrap's $line-height-base
}

.p-menu-item.standard-sentence-option.active .p-menu-item-content {
  border-radius: 0;
}

/* PrimeVue .p-button gap sits on top of .mimic-bv-caret::after margin-left. */
.p-button.sentence-dropdown-button {
  gap: 0;
}
</style>
